import { useEffect, useState } from 'react';
import axios from 'axios';
import { Award, Download, FileText, Search, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from 'recharts';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { FiveStarSettingsCard } from '../components/five-star/FiveStarSettingsCard';
import { StarColorsCard } from '../components/five-star/StarColorsCard';
import { AuditorPerformance } from '../components/five-star/AuditorPerformance';
import { ScheduleCard } from '../components/five-star/ScheduleCard';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { API, StarScore, fmtDate, openAuditPdf } from '../components/five-star/stars';
import { GoldHistory, GoldHistoryTab } from '../components/five-star/GoldHistory';

const useDetail = (url) => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(url).then(r => setD(r.data)).catch(() => setD(false)); }, [url]);
  return d;
};

const AuditLine = ({ a, score }) => (
  <button type="button" onClick={() => openAuditPdf(a.id)} className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50" data-testid={`eval-audit-${a.id}`}>
    <span className="min-w-0"><FileText className="mr-1 inline h-3.5 w-3.5 text-slate-400" /><b className="font-mono text-xs">{a.request_number}</b> · {fmtDate(a.created_at)} · {a.company_name ? `${a.company_name} · ` : ''}{a.is_tipi} / {a.is_cesidi}{a.task ? ` · ${a.task}` : ''} <span className="text-slate-500">— {a.job_description}</span></span>
    <StarScore value={score ?? a.score} />
  </button>
);

const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const mLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

export const Trend = ({ trend, hideThreshold = false }) => {
  if (!trend.length) return null;
  const diff = trend.length > 1 ? trend[trend.length - 1].avg - trend[trend.length - 2].avg : 0;
  const Icon = diff >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="rounded-lg border p-3" data-testid="eval-trend">
      <div className="mb-1 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">Aylara Göre Puan</p>
        {trend.length > 1 && <span className={`flex items-center gap-1 text-xs font-semibold ${diff >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="eval-trend-diff"><Icon className="h-4 w-4" />{diff >= 0 ? 'İyileşiyor' : 'Kötüleşiyor'} ({diff >= 0 ? '+' : ''}{diff.toFixed(2)})</span>}
      </div>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend.map(t => ({ ...t, label: mLabel(t.month) }))} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
            {!hideThreshold && <ReferenceLine y={2.5} stroke="#fda4af" strokeDasharray="4 4" />}
            <Tooltip formatter={(v, n, p) => [`${v} / 5 (${p.payload.audits} denetim)`, 'Ortalama']} />
            <Line type="monotone" dataKey="avg" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4, fill: '#f59e0b' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const download = async (kind, r) => {
  try {
    const res = await axios.get(`${API}/five-star/rankings/${kind}/${r.id}/pdf`, { responseType: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(res.data); a.download = `five_star_karne_${r.name}.pdf`; a.click();
  } catch { toast.error('Karne PDF oluşturulamadı'); }
};

const isGold = (c) => !!c.gold;
export const GoldBadge = ({ id, label = 'Altın Firma' }) => (
  <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-yellow-500 bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 px-2.5 py-0.5 text-[11px] font-bold text-amber-950 shadow-[0_0_8px_rgba(250,204,21,0.6)]" data-testid={`gold-badge-${id}`}>
    <Award className="h-3.5 w-3.5" />{label}
  </span>
);

export const Repeats = ({ rows }) => (
  <div data-testid="eval-repeats">
    <p className="mb-1 text-sm font-semibold text-slate-800">Tekrarlayan Eksiklikler <span className="font-normal text-slate-500">(aynı personel, aynı başlık, art arda düşük puan)</span></p>
    {rows.length === 0 ? <p className="rounded-lg border p-3 text-sm text-emerald-700">Tekrarlayan eksiklik yok</p> : (
      <div className="divide-y rounded-lg border">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`eval-repeat-${i}`}>
            <span><b>{r.employee}</b> · {r.criterion} <span className="text-slate-500">· {r.count} kez art arda ({r.scores.join(' → ')}) · {fmtDate(r.first_at)} – {fmtDate(r.last_at)}</span></span>
            <span className={`rounded px-2 py-0.5 text-xs font-bold ${r.ongoing ? 'bg-red-600 text-white' : 'bg-emerald-100 text-emerald-700'}`}>{r.ongoing ? 'Devam ediyor' : 'Düzeldi'}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Certificate = ({ c, kind = 'companies' }) => {
  const label = kind === 'companies' ? 'Altın Firma' : 'Altın Personel';
  const [sentAt, setSentAt] = useState(c.gold_certificate_sent_at);
  const [busy, setBusy] = useState(false);
  const preview = async () => {
    try { const r = await axios.get(`${API}/five-star/rankings/${kind}/${c.id}/certificate`, { responseType: 'blob' }); window.open(URL.createObjectURL(r.data), '_blank'); } catch { toast.error('Sertifika oluşturulamadı'); }
  };
  const send = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/five-star/rankings/${kind}/${c.id}/certificate/send`); setSentAt(new Date().toISOString()); toast.success(`Sertifika gönderildi: ${data.sent_to.join(', ')}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gönderilemedi'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-100 px-4 py-3 text-sm" data-testid={`gold-certificate-${c.id}`}>
      <Award className="h-5 w-5 text-amber-600" />
      <span className="flex-1 text-amber-900"><b>{label} teşekkür sertifikası</b>{kind === 'employees' && ' (firma yetkilisine)'} · {sentAt ? `Gönderildi: ${fmtDate(sentAt)}` : `Henüz gönderilmedi (ilk ${label} olduğunda otomatik gönderilir)`}</span>
      <Button size="sm" variant="outline" className="bg-white" onClick={preview} data-testid={`gold-cert-preview-${c.id}`}>Önizle</Button>
      <Button size="sm" className="bg-amber-500 text-amber-950 hover:bg-amber-400" disabled={busy} onClick={send} data-testid={`gold-cert-send-${c.id}`}>{sentAt ? 'Tekrar Gönder' : 'E-postayla Gönder'}</Button>
    </div>
  );
};

const CompanyDetail = ({ c }) => {
  const d = useDetail(`${API}/five-star/rankings/companies/${c.id}`);
  if (!d) return <p className="text-sm text-slate-400">{d === false ? 'Yüklenemedi' : 'Yükleniyor...'}</p>;
  return (
    <div className="space-y-4">
      <div className="grid gap-2 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-3">
        <div><span className="text-xs text-slate-500">Ticari Unvan</span><p className="font-medium">{c.trade_name || '-'}</p></div>
        <div><span className="text-xs text-slate-500">NACE Kodu</span><p className="font-medium">{c.nace_kodu || '-'}</p></div>
        <div><span className="text-xs text-slate-500">Faaliyet / İş Tanımı</span><p className="font-medium">{c.activity_description || '-'}</p></div>
      </div>
      {c.gold && <Certificate c={c} />}
      <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => download('companies', c)} data-testid={`eval-card-pdf-${c.id}`}><Download className="mr-1 h-4 w-4" />Karne PDF İndir</Button></div>
      <Trend trend={d.trend || []} />
      <Repeats rows={d.repeats || []} />
      <GoldHistory entityId={c.id} />
      <div>
        <p className="mb-1 text-sm font-semibold text-slate-800">Yaptığı İşler (en çok yapılan önce)</p>
        <div className="divide-y rounded-lg border">
          {d.jobs.map(j => (
            <div key={`${j.is_tipi}-${j.is_cesidi}`} className="px-3 py-2 text-sm">
              <div className="flex justify-between"><b>{j.is_tipi} / {j.is_cesidi}</b><span className="rounded bg-blue-50 px-2 text-xs font-semibold text-blue-700">{j.count} iş</span></div>
              {j.descriptions.length > 0 && <p className="text-xs text-slate-500">{j.descriptions.map(x => `${x.text}${x.count > 1 ? ` (${x.count})` : ''}`).join(' · ')}</p>}
            </div>
          ))}
          {d.jobs.length === 0 && <p className="p-3 text-sm text-slate-400">Onaylı iş yok</p>}
        </div>
      </div>
      <div><p className="mb-1 text-sm font-semibold text-slate-800">Denetimler</p>{d.audits.map(a => <AuditLine key={a.id} a={a} />)}</div>
    </div>
  );
};

const EmployeeDetail = ({ e }) => {
  const d = useDetail(`${API}/five-star/rankings/employees/${e.id}`);
  if (!d) return <p className="text-sm text-slate-400">{d === false ? 'Yüklenemedi' : 'Yükleniyor...'}</p>;
  return (
    <div className="space-y-3">
      {e.gold && <Certificate c={e} kind="employees" />}
      <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => download('employees', e)} data-testid={`eval-emp-pdf-${e.id}`}><Download className="mr-1 h-4 w-4" />Karne PDF İndir</Button></div>
      <GoldHistory entityId={e.id} />
      <div><p className="mb-1 text-sm font-semibold text-slate-800">Denetimler</p>{d.map(a => <AuditLine key={a.id} a={a} />)}</div>
    </div>
  );
};

const RankList = ({ rows, render, detail, prefix }) => (
  <Accordion type="single" collapsible className="rounded-lg border bg-white">
    {rows.map((r, i) => (
      <AccordionItem key={r.id} value={r.id} className="px-4" data-testid={`${prefix}-${r.id}`}>
        <AccordionTrigger className="hover:no-underline">
          <div className="flex flex-1 items-center gap-4 text-left">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
            <div className="min-w-0 flex-1">{render(r)}</div>
            <div className="mr-3 text-right"><StarScore value={r.avg} jobs={r.jobs} lowData={r.low_data} /><p className="text-[11px] text-slate-400">Güvenilir puan: {r.reliable} · {r.audits} denetim</p></div>
          </div>
        </AccordionTrigger>
        <AccordionContent>{detail(r)}</AccordionContent>
      </AccordionItem>
    ))}
    {rows.length === 0 && <p className="p-8 text-center text-sm text-slate-500">Henüz denetim yapılmamış</p>}
  </Accordion>
);

export default function FirmEvaluationPage() {
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState('');
  const [cfg, setCfg] = useState(null);
  const { user, canView, canTab } = useAuth();
  const load = () => {
    axios.get(`${API}/five-star/rankings/companies`).then(r => setCompanies(r.data)).catch(() => {});
    axios.get(`${API}/five-star/rankings/employees`).then(r => setEmployees(r.data)).catch(() => {});
    axios.get(`${API}/five-star/settings`).then(r => setCfg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const tr = (v) => String(v ?? '').replace('.', ',');
  const s = q.trim().toLocaleLowerCase('tr');
  const f = (rows, keys) => rows.filter(r => !s || keys.some(k => (r[k] || '').toLocaleLowerCase('tr').includes(s)));
  return (
    <div className="space-y-6" data-testid="firm-evaluation-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Award className="h-6 w-6 text-amber-500" />Firma Değerlendirme</h1>
        <p className="text-sm text-slate-500">Five Star denetimlerine göre firma ve personel karnesi. Sıralama, az işi olanı genel ortalamaya yaklaştıran güvenilir puana göredir; 3 işten azında "Az veri" yazar. {cfg && <>Ortalaması {tr(cfg.gold_avg)} ve üzeri, en az {cfg.gold_jobs} işi olan firmalar "Altın Firma", personel "Altın Personel" rozeti alır.</>}</p>
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Firma, personel veya T.C. ara..." className="pl-9" data-testid="eval-search" />
      </div>
      <Tabs defaultValue={['firma', 'personel', 'gecmis', 'uzman', 'ayarlar'].find(t => canTab('firm_evaluation', t))}>
        <TabsList>{canTab('firm_evaluation', 'firma') && <TabsTrigger value="firma" data-testid="eval-tab-firma">Firmalar ({companies.length})</TabsTrigger>}{canTab('firm_evaluation', 'personel') && <TabsTrigger value="personel" data-testid="eval-tab-personel">Personel ({employees.length})</TabsTrigger>}{canTab('firm_evaluation', 'gecmis') && <TabsTrigger value="gecmis" data-testid="eval-tab-gecmis">Rozet Geçmişi</TabsTrigger>}{canTab('firm_evaluation', 'uzman') && <TabsTrigger value="uzman" data-testid="eval-tab-uzman">Uzman Performansı</TabsTrigger>}{canTab('firm_evaluation', 'ayarlar') && <TabsTrigger value="ayarlar" data-testid="eval-tab-ayarlar">Ayarlar</TabsTrigger>}</TabsList>
        <TabsContent value="firma" className="mt-4">
          <RankList prefix="eval-company" rows={f(companies, ['name', 'trade_name'])} detail={c => <CompanyDetail c={c} />}
            render={c => <><p className="flex items-center font-semibold text-slate-900">{c.name}{isGold(c) && <GoldBadge id={c.id} />}</p><p className="truncate text-xs text-slate-500">{c.activity_description || c.trade_name}</p></>} />
        </TabsContent>
        <TabsContent value="personel" className="mt-4">
          <RankList prefix="eval-employee" rows={f(employees, ['name', 'tc', 'company_name'])} detail={e => <EmployeeDetail e={e} />}
            render={e => <><p className="flex items-center font-semibold text-slate-900">{e.name} <span className="ml-1 font-mono text-xs font-normal text-slate-500">{e.tc}</span>{e.gold && <GoldBadge id={e.id} label="Altın Personel" />}</p><p className="text-xs text-slate-500">{e.position || '-'} · {e.company_name}</p></>} />
        </TabsContent>
        <TabsContent value="gecmis" className="mt-4"><GoldHistoryTab /></TabsContent>
        <TabsContent value="uzman" className="mt-4"><AuditorPerformance /></TabsContent>
        {canTab('firm_evaluation', 'ayarlar') && <TabsContent value="ayarlar" className="mt-4 space-y-6"><FiveStarSettingsCard onSaved={load} /><ScheduleCard /><StarColorsCard /></TabsContent>}
      </Tabs>
    </div>
  );
}
